import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import { useServices } from '../hooks/useAPI';

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: services, isLoading, error } = useServices();

  // Default services if API fails
  const defaultServices = [
    {
      id: 1,
      title: 'ERP Systems',
      shortDescription: 'Custom enterprise resource planning systems designed to unify your business processes.',
      slug: 'erp-systems'
    },
    {
      id: 2,
      title: 'AI/ML Solutions',
      shortDescription: 'Leverage intelligent automation and predictive analytics to gain a competitive edge.',
      slug: 'ai-ml-solutions'
    },
    {
      id: 3,
      title: 'Cybersecurity',
      shortDescription: 'Comprehensive security audits and threat detection to safeguard your digital assets.',
      slug: 'cybersecurity'
    },
    {
      id: 4,
      title: 'Digital Marketing',
      shortDescription: 'Data-driven strategies to grow your online presence and drive measurable results.',
      slug: 'digital-marketing'
    },
    {
      id: 5,
      title: 'Corporate Training',
      shortDescription: 'Upskill your team with tailored courses in modern technology and best practices.',
      slug: 'corporate-training'
    },
    {
      id: 6,
      title: 'Research & Development',
      shortDescription: 'Innovating for the future by exploring emerging technologies and creating next-gen solutions.',
      slug: 'research-development'
    }
  ];

  const handleServiceClick = (slug) => {
    window.location.href = `/services/${slug}`;
  };

  const displayServices = services && services.length > 0 ? services : defaultServices;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="services" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6 text-center" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR SERVICES
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            End-to-End Technology Solutions
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600 mb-12"
          >
            We provide a comprehensive suite of services to power your business's digital transformation.
          </motion.p>

          {isLoading ? (
            <div className="flex justify-center">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
              {displayServices.map((service, index) => (
                <motion.div
                  key={service.id || index}
                  variants={cardVariants}
                  custom={index}
                  className="bg-lion-gray p-8 rounded-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 cursor-pointer group"
                  onClick={() => handleServiceClick(service.slug)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-poppins font-bold text-lion-blue group-hover:text-lion-yellow transition-colors duration-300">
                      {service.title}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-lion-yellow group-hover:transform group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {service.shortDescription}
                  </p>
                  
                  <div className="mt-6 flex items-center text-lion-blue group-hover:text-lion-yellow transition-colors duration-300">
                    <span className="text-sm font-medium">Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:transform group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {error && (
            <motion.p 
              variants={itemVariants}
              className="text-gray-500 text-center mt-8"
            >
              Services will be loaded shortly. Please check back soon.
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;